import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import FFTKpiPanel from "@/components/FFTKpiPanel";
import KpiViolationsPanel from "@/components/KpiViolationsPanel";

const RISK_COLORS = { HIGH: "#DC2626", MEDIUM: "#EA580C", LOW: "#9CA3AF", UNKNOWN: "#E5E7EB" };

export default function ManagerDashboard() {
  const [dist, setDist] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/analytics/risk-distribution"),
        api.get("/review-requests"),
      ]);
      setDist(a.data);
      setReviews(b.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markReviewed = async (id) => {
    try {
      await api.patch(`/review-requests/${id}`, { status: "reviewed" });
      toast.success("Marked reviewed");
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const chartData = dist
    ? Object.entries(dist.distribution).map(([k, v]) => ({ name: k, count: v }))
    : [];

  const totalVAR = dist
    ? (dist.value_at_risk.HIGH || 0) + (dist.value_at_risk.MEDIUM || 0)
    : 0;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8" data-testid="manager-dashboard">
      <div className="mb-8">
        <div className="overline text-gray-400 mb-2">Manager Control Room</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
          Portfolio Risk
        </h1>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <MetricCard
          label="Total Deals"
          value={dist?.total_deals ?? "—"}
          testid="metric-total"
        />
        <MetricCard
          label="High Risk"
          value={dist?.distribution.HIGH ?? 0}
          accent="#DC2626"
          testid="metric-high"
        />
        <MetricCard
          label="Medium Risk"
          value={dist?.distribution.MEDIUM ?? 0}
          accent="#EA580C"
          testid="metric-medium"
        />
        <MetricCard
          label="Value at Risk"
          value={`$${Number(totalVAR).toLocaleString()}`}
          testid="metric-var"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="border border-gray-200 bg-white rounded-sm p-6 lg:col-span-1">
          <div className="overline text-gray-400 mb-4">Risk Distribution</div>
          <div style={{ width: "100%", height: 240 }}>            <ResponsiveContainer>
              <BarChart data={chartData}>
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "#E5E7EB" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "#E5E7EB" }}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: "#F3F4F6" }}
                  contentStyle={{
                    border: "1px solid #E5E7EB",
                    borderRadius: 2,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                  {chartData.map((e, i) => (
                    <Cell key={i} fill={RISK_COLORS[e.name] || "#000"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-gray-200 bg-white rounded-sm p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="overline text-gray-400">Pending Review Requests</div>
            <span className="metric-value text-sm text-gray-400">
              {reviews.filter((r) => r.status === "pending").length}
            </span>
          </div>
          {loading ? (
            <div className="text-sm text-gray-400">Loading…</div>
          ) : reviews.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">
              No review requests yet. Reps trigger these from high-risk deals.
            </div>
          ) : (
            <div className="space-y-3" data-testid="reviews-list">
              {reviews.map((r) => (
                <div
                  key={r.id}
                  className="border border-gray-200 rounded-sm p-4 hover:border-black transition-colors"
                  data-testid={`review-row-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {r.analysis?.risk_level && (
                          <span className={`risk-pill ${r.analysis.risk_level.toLowerCase()}`}>
                            {r.analysis.risk_level}
                          </span>
                        )}
                        <span className="font-medium text-sm truncate">{r.deal_name}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {r.company} · <span className="metric-value">${Number(r.deal_value || 0).toLocaleString()}</span> · from {r.requester?.name}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${
                      r.status === "pending" ? "bg-gray-100 text-gray-700" :
                      r.status === "reviewed" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-400"
                    }`}>
                      {r.status}
                    </span>
                  </div>
                  {r.note && (
                    <p className="text-sm text-gray-700 my-3">{r.note}</p>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <Link
                      to={`/app/deals/${r.deal_id}`}
                      className="text-xs inline-flex items-center gap-1 text-gray-700 hover:text-black"
                      data-testid={`open-review-deal-${r.id}`}
                    >
                      Open deal <ArrowUpRight className="w-3 h-3" />
                    </Link>
                    {r.status === "pending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markReviewed(r.id)}
                        data-testid={`mark-reviewed-${r.id}`}
                        className="rounded-sm h-7 text-xs gap-1 ml-auto"
                      >
                        <Check className="w-3 h-3" /> Mark reviewed
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <FFTKpiPanel />
        </div>
        <div className="lg:col-span-2">
          <KpiViolationsPanel />
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, accent, testid }) {
  return (
    <div
      className="border border-gray-200 bg-white rounded-sm p-5"
      style={accent ? { borderLeftColor: accent, borderLeftWidth: 3 } : {}}
      data-testid={testid}
    >
      <div className="overline text-gray-400 mb-2">{label}</div>
      <div className="metric-value text-3xl tracking-tight">{value}</div>
    </div>
  );
}
